import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Assessment as ReportingIcon,
  ContactPage as DirectoryIcon,
  Folder as ResourcesIcon,
  ThumbUp as ApprovalIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const MobileBottomNavigation = ({ onMenuClick, sx }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Don't render on desktop
  if (!isMobile) return null;

  const isAdmin = user?.role === 'admin';
  const isTeamLead = user?.role === 'team_lead';
  const isUser = user?.role === 'user';
  const isAccountant = user?.role === 'accountant';

  // Build navigation items based on user role
  const navigationItems = [];

  // Regular user and team lead have Timecard
  if (isUser || isTeamLead) {
    navigationItems.push({
      label: 'Timecard',
      value: '/timecard',
      icon: <TimeIcon />,
    });
  }

  // All roles have Reporting
  navigationItems.push({
    label: 'Reports',
    value: '/reporting',
    icon: <ReportingIcon />,
  });

  // All roles except Accountant have Directory
  if (!isAccountant) {
    navigationItems.push({
      label: 'Directory',
      value: '/directory',
      icon: <DirectoryIcon />,
    });
  }

  // All roles except Accountant have Resources
  if (!isAccountant) {
    navigationItems.push({
      label: 'Resources',
      value: '/resources',
      icon: <ResourcesIcon />,
    });
  }

  // Only show menu if user has access to additional menu items
  const hasMenuItems = isAdmin || isTeamLead;
  if (hasMenuItems) {
    navigationItems.push({
      label: 'Menu',
      value: 'menu',
      icon: <MenuIcon />,
    });
  }

  const getCurrentValue = () => {
    const currentPath = location.pathname;
    const matchingItem = navigationItems.find(item => 
      item.value !== 'menu' && currentPath === item.value
    );
    return matchingItem ? matchingItem.value : false;
  };

  const handleChange = (event, newValue) => {
    if (newValue === 'menu') {
      onMenuClick();
    } else {
      navigate(newValue);
    }
  };

  return (
    <Paper 
      sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1500, // Very high z-index to ensure it's always on top
        borderTop: 1,
        borderColor: 'divider',
        paddingBottom: 'env(safe-area-inset-bottom)', // Account for mobile safe area
        isolation: 'isolate', // Create new stacking context
        ...sx, // Apply any additional styles passed via sx prop
      }} 
      elevation={8} // Higher elevation for better visual separation
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 72, // Reduced height but still accommodating
          paddingTop: '4px',
          paddingBottom: '4px',
          position: 'relative',
          zIndex: 1500, // Ensure navigation itself has high z-index
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 6px', // Balanced padding
            maxWidth: 'none',
            minHeight: '60px', // Slightly reduced
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center', // Center content instead of space-between
            alignItems: 'center',
            gap: '2px', // Small gap between icon and label
            position: 'relative', // Ensure proper positioning
            zIndex: 1500, // Ensure each action has high z-index
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.6rem',
            marginTop: '0px', // Remove margin since we're using gap
            lineHeight: 1,
            whiteSpace: 'nowrap', // Prevent text wrapping
            '&.Mui-selected': {
              fontSize: '0.6rem',
            },
          },
          '& .MuiBottomNavigationAction-icon': {
            fontSize: '1.2rem', // Keep icon size
            marginBottom: '0px',
          },
        }}
      >
        {navigationItems.map((item) => (
          <BottomNavigationAction
            key={item.value}
            label={item.label}
            value={item.value}
            icon={item.icon}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNavigation;
